import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import api from "@/lib/api";
import { 
  Languages, Download, FileJson, FileSpreadsheet, 
  Globe, CheckCircle, Clock, AlertCircle, RefreshCw,
  Copy, Loader2
} from "lucide-react";

export default function TranslationExportPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [status, setStatus] = useState(null);
  const [languages, setLanguages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [progress, setProgress] = useState(null);
  
  // Export settings
  const [selectedLanguage, setSelectedLanguage] = useState("es");
  const [selectedFormat, setSelectedFormat] = useState("json");
  const [selectedCategories, setSelectedCategories] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statusRes, langRes, catRes, progressRes] = await Promise.all([
        api.get("/ehr/translation-export/status"),
        api.get("/ehr/translation-export/languages"),
        api.get("/ehr/translation-export/categories"),
        api.get("/ehr/translation-export/progress")
      ]);
      
      setStatus(statusRes.data);
      setLanguages(langRes.data.languages);
      setCategories(catRes.data.categories);
      setProgress(progressRes.data);
      
      // Select all categories by default
      setSelectedCategories(catRes.data.categories.map(c => c.name));
    } catch (error) {
      console.error("Error loading translation data:", error);
      toast.error("Failed to load translation data");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      
      const endpoint = selectedFormat === "csv" 
        ? `/ehr/translation-export/export/csv/${selectedLanguage}`
        : `/ehr/translation-export/export/json/${selectedLanguage}`;
      
      const params = selectedCategories.length < categories.length 
        ? `?categories=${selectedCategories.join(",")}`
        : "";
      
      const response = await api.get(`${endpoint}${params}`);
      
      if (selectedFormat === "csv") {
        // Download CSV
        const blob = new Blob([response.data.csv_content], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = response.data.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success(`CSV exported: ${response.data.total_keys} keys`);
      } else {
        // Download JSON
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: "application/json" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `translations_${selectedLanguage}_${new Date().toISOString().split("T")[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success(`JSON exported: ${response.data.total_keys} keys`);
      }
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export translations");
    } finally {
      setExporting(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "complete": return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "in_progress": return <Clock className="w-4 h-4 text-amber-500" />;
      default: return <AlertCircle className="w-4 h-4 text-red-500" />;
    }
  };

  const getStatusColor = (percent) => {
    if (percent >= 90) return "bg-green-500";
    if (percent >= 50) return "bg-amber-500";
    return "bg-red-500";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white p-6" data-testid="translation-export-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-3">
              <Languages className="w-7 h-7 text-primary" />
              Translation Export Center
            </h1>
            <p className="text-muted-foreground">
              Export translation keys for professional translators
            </p>
          </div>
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
        
        {/* Stats */}
        {status && (
          <div className="grid grid-cols-4 gap-4 mt-6">
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-primary">{status.total_keys}</div>
                <p className="text-sm text-muted-foreground">Total Keys</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-green-600">{status.total_categories}</div>
                <p className="text-sm text-muted-foreground">Categories</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-purple-600">{status.supported_languages}</div>
                <p className="text-sm text-muted-foreground">Languages</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-amber-600">
                  {progress?.languages?.es?.progress_percent || 0}%
                </div>
                <p className="text-sm text-muted-foreground">Spanish Complete</p>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      <Tabs defaultValue="export" className="space-y-6">
        <TabsList>
          <TabsTrigger value="export">
            <Download className="w-4 h-4 mr-2" />
            Export
          </TabsTrigger>
          <TabsTrigger value="progress">
            <Globe className="w-4 h-4 mr-2" />
            Progress
          </TabsTrigger>
          <TabsTrigger value="categories">
            <FileJson className="w-4 h-4 mr-2" />
            Categories
          </TabsTrigger>
        </TabsList>

        {/* Export Tab */}
        <TabsContent value="export">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Export Settings */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Export Settings</CardTitle>
                <CardDescription>
                  Configure your export for professional translators
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Language Selection */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Target Language</label>
                  <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
                    <SelectTrigger data-testid="language-select">
                      <SelectValue placeholder="Select language" />
                    </SelectTrigger>
                    <SelectContent>
                      {languages.filter(l => l.code !== "en").map(lang => (
                        <SelectItem key={lang.code} value={lang.code}>
                          <div className="flex items-center gap-2">
                            <span>{lang.name}</span>
                            <span className="text-muted-foreground">({lang.native})</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Format Selection */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Export Format</label>
                  <div className="flex gap-4">
                    <Button
                      variant={selectedFormat === "json" ? "default" : "outline"}
                      onClick={() => setSelectedFormat("json")}
                      className="flex-1"
                    >
                      <FileJson className="w-4 h-4 mr-2" />
                      JSON
                    </Button>
                    <Button
                      variant={selectedFormat === "csv" ? "default" : "outline"}
                      onClick={() => setSelectedFormat("csv")}
                      className="flex-1"
                    >
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      CSV (Spreadsheet)
                    </Button>
                  </div>
                </div>

                {/* Category Selection */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium">Categories to Export</label>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        if (selectedCategories.length === categories.length) {
                          setSelectedCategories([]);
                        } else {
                          setSelectedCategories(categories.map(c => c.name));
                        }
                      }}
                    >
                      {selectedCategories.length === categories.length ? "Deselect All" : "Select All"}
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {categories.map(cat => (
                      <div 
                        key={cat.name}
                        className="flex items-center space-x-2 p-2 border rounded hover:bg-muted/50"
                      >
                        <Checkbox
                          id={cat.name}
                          checked={selectedCategories.includes(cat.name)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedCategories([...selectedCategories, cat.name]);
                            } else {
                              setSelectedCategories(selectedCategories.filter(c => c !== cat.name));
                            }
                          }}
                        />
                        <label htmlFor={cat.name} className="text-sm cursor-pointer flex-1">
                          {cat.name}
                          <span className="text-xs text-muted-foreground ml-1">
                            ({cat.key_count})
                          </span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Export Button */}
                <Button 
                  onClick={handleExport} 
                  disabled={exporting || selectedCategories.length === 0}
                  className="w-full"
                  size="lg"
                  data-testid="export-btn"
                >
                  {exporting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Export {selectedCategories.reduce((acc, cat) => {
                    const found = categories.find(c => c.name === cat);
                    return acc + (found?.key_count || 0);
                  }, 0)} Keys as {selectedFormat.toUpperCase()}
                </Button>
              </CardContent>
            </Card>

            {/* Instructions */}
            <Card>
              <CardHeader>
                <CardTitle>Instructions for Translators</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-800 mb-2">JSON Format</h4>
                  <p className="text-blue-700">
                    Each key has an "en" field with English text and an empty target language field 
                    to fill in.
                  </p>
                </div>
                
                <div className="p-3 bg-green-50 rounded-lg">
                  <h4 className="font-medium text-green-800 mb-2">CSV Format</h4>
                  <p className="text-green-700">
                    Opens in Excel/Google Sheets. Fill the "Translation" column and return the file.
                  </p>
                </div>

                <div className="p-3 bg-amber-50 rounded-lg">
                  <h4 className="font-medium text-amber-800 mb-2">Variables</h4>
                  <p className="text-amber-700">
                    Keep {"{{variable}}"} placeholders unchanged. They insert dynamic values.
                  </p>
                </div>

                <div className="p-3 bg-purple-50 rounded-lg">
                  <h4 className="font-medium text-purple-800 mb-2">Context</h4>
                  <p className="text-purple-700">
                    This is a hospice care application. Tone should be compassionate and professional.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Progress Tab */}
        <TabsContent value="progress">
          <Card>
            <CardHeader>
              <CardTitle>Translation Progress by Language</CardTitle>
              <CardDescription>
                Track completion status for each supported language
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {progress && Object.entries(progress.languages).map(([code, lang]) => (
                  <div key={code} className="flex items-center gap-4 p-4 border rounded-lg">
                    <div className="w-32">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(lang.status)}
                        <span className="font-medium">{lang.language}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">{lang.native}</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between text-sm mb-1">
                        <span>{lang.translated_estimate} of {lang.total_keys} keys</span>
                        <span className="font-medium">{lang.progress_percent}%</span>
                      </div>
                      <Progress value={lang.progress_percent} className={getStatusColor(lang.progress_percent)} />
                    </div>
                    <Badge variant={
                      lang.status === "complete" ? "default" : 
                      lang.status === "in_progress" ? "secondary" : "destructive"
                    }>
                      {lang.status.replace("_", " ")}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map(cat => (
              <Card key={cat.name}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center justify-between">
                    {cat.name}
                    <Badge variant="outline">{cat.key_count} keys</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="max-h-48 overflow-y-auto space-y-1">
                    {cat.keys.slice(0, 10).map(key => (
                      <div 
                        key={key}
                        className="text-xs p-1.5 bg-muted/50 rounded flex items-center justify-between group"
                      >
                        <code className="truncate flex-1">{key}</code>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5 opacity-0 group-hover:opacity-100"
                          onClick={() => copyToClipboard(key)}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                    {cat.keys.length > 10 && (
                      <p className="text-xs text-muted-foreground text-center pt-2">
                        +{cat.keys.length - 10} more keys
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
